import React, { FC, useState } from 'react'
import { MediaTextContainer } from './style'

//* components
import GlobalContainer from '../globalContainer'
import { ImageContainer } from './subcomponents/imageContainer'
import MediaTextModal from './subcomponents/modal'
import { TextContainer } from './subcomponents/textContainer'

//* utils
import { ModuleNames } from '../../../constants/componentNames.constants'
import { ComponentMediaText } from '../../../models/components/mediaText'

type Props = {
  data: ComponentMediaText
  hasMediaAsset?: boolean
  pageType?: string
}

const MediaText: FC<Props> = ({ data, hasMediaAsset, pageType = '' }) => {
  if (!data) return null
  const [open, setOpen] = useState(false)
  const handleClose = () => setOpen(false)
  const handleOpen = () => setOpen(true)

  const mediaTextData = {
    body: data.body || '',
    headline: data.headline || '',
    subtitle: data.subtitle || '',
    cta: data.assetTextCta || data.cta || null,
    imageAlignment: data.imageAlignment || 'left',
    theme: data.assetTextTheme || data.theme || 'light',
    mediaStyle: data.mediaStyle || 'Photo',
    asset: data.asset
      ? {
          __typename: data.asset.__typename,
          imageUrl: data.asset?.image?.file?.url || '',
          altText: data.asset.altText,
          start: '',
          animationFile: 'animationFile' in data.asset ? data.asset.animationFile : {},
          youtubeId: 'youtubeId' in data.asset ? data.asset.youtubeId : '',
          vimeoId: 'vimeoId' in data.asset ? data.asset.vimeoId : '',
          videoUrl: 'videoUrl' in data.asset ? data.asset.videoUrl : '',
          tencentUrl: 'tencentVideoUrl' in data.asset ? data.asset.tencentVideoUrl : '',
          url: data.asset.image?.file?.url || '',
          imageOverlay: 'imageOverlay' in data.asset ? data.asset.imageOverlay : '',
        }
      : {},
    topPadding: data.topPadding || 'None',
    bottomPadding: data.bottomPadding || 'Large',
    backgroundUrl: data.background?.image?.file?.url || '',
    playInPlace: data.playInPlace || false,
    useCase: data.useCase || '',
  }
  if (mediaTextData.useCase === 'Events') {
    if (mediaTextData.asset.__typename == 'ContentfulWrapperAsset') {
      mediaTextData.mediaStyle = 'Photo'
    }

    mediaTextData.theme = 'Orange'
  }
  const video = {
    id: '',
    type: '',
    url: '',
    animation: false,
    animationMP4: false,
    start: '',
    imageOverlay: '',
  }

  if (hasMediaAsset) {
    if (mediaTextData.asset.__typename === ModuleNames.WRAPPER_VIDEO) {
      mediaTextData.mediaStyle = 'Video'
    }

    mediaTextData.theme = 'Grey'
  }

  if (mediaTextData.asset.__typename === ModuleNames.WRAPPER_VIDEO) {
    if (mediaTextData.asset.youtubeId) {
      video.id = mediaTextData.asset.youtubeId
      video.type = 'youtube'
    } else if (mediaTextData.asset.vimeoId) {
      video.id = mediaTextData.asset.vimeoId
      video.type = 'vimeo'
    } else if (mediaTextData.asset.videoUrl) {
      video.url = mediaTextData.asset.videoUrl
      video.type = 'html-video'
    } else if (mediaTextData.asset.tencentUrl) {
      video.url = mediaTextData.asset.tencentUrl
      video.type = 'tencent-video'
    }

    const animationFileUrl = mediaTextData.asset.animationFile?.file?.url || ''
    if (animationFileUrl && mediaTextData.mediaStyle === 'Animation') {
      mediaTextData.asset.url = animationFileUrl
      let last = mediaTextData.asset.url.lastIndexOf('.')
      if (mediaTextData.asset.url.substring(last).includes('.mp4')) video.animationMP4 = true
      video.animation = true
    }

    video.start = mediaTextData.asset.start
    video.imageOverlay = mediaTextData.asset.imageOverlay || ''
  }

  const hasNoVideoDetail = mediaTextData.mediaStyle === 'Video' && !video.id && !video.url
  const hasNoAnimation = mediaTextData.mediaStyle === 'Animation' && !video.animation
  const hasNoVideoOrAnimation =
    mediaTextData.asset.__typename === ModuleNames.WRAPPER_VIDEO &&
    mediaTextData.mediaStyle !== 'Video' &&
    mediaTextData.mediaStyle !== 'Animation'
  if (hasNoVideoDetail || hasNoAnimation || hasNoVideoOrAnimation) return null

  const enlargable =
    mediaTextData.mediaStyle === 'Enlargable' || mediaTextData.mediaStyle === 'Animation'

  return (
    <MediaTextContainer
      data-cy="mediaText"
      $background={mediaTextData.theme}
      $backgroundUrl={mediaTextData.backgroundUrl}
      $alignment={mediaTextData.imageAlignment}
      $enableClickToEnlarge={enlargable}
      $topPadding={mediaTextData.topPadding}
      $bottomPadding={mediaTextData.bottomPadding}
      $useCase={mediaTextData.useCase}
      $mediaStyle={mediaTextData.mediaStyle}
      $hasMediaAsset={!!hasMediaAsset}
      $pageType={pageType}
      className={`${data.useCase === 'Full width' ? 'MediaText--fullWidth' : ''}${
        data.useCase === 'Events' ? 'MediaText--Events__main' : ''
      } `}>
      <GlobalContainer
        className={`${
          data.useCase === 'Full width'
            ? 'MediaText__Outer-Container--fullWidth'
            : 'MediaText__Outer-Container'
        } ${data.useCase === 'Events' ? 'MediaText--Events orangeTheme' : ''}`}
        maxWidth={false}>
        <div className={`MediaText__Inner-Container ${hasMediaAsset ? 'MediaAsset' : ''}`}>
          {enlargable && !video.id && (
            <MediaTextModal
              mediaTextData={mediaTextData}
              video={video}
              handleClose={handleClose}
              open={open}
            />
          )}

          <ImageContainer
            mediaTextData={mediaTextData}
            video={video}
            handleOpen={handleOpen}
            enlargable={enlargable}
          />

          {!hasMediaAsset && <TextContainer mediaTextData={mediaTextData} />}
        </div>
      </GlobalContainer>
    </MediaTextContainer>
  )
}

export default MediaText
